// src/pages/admin/NoAdminAccessPage.tsx
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "features/auth/hooks/useAuth";
import { getErrorMessage } from "utils/errors";

/**
 * Shown to a signed-in account that cannot use the admin area: no admin
 * rights, or an email address that has not been verified. An unverified
 * account can ask for the verification email again from here, since there is
 * no other page for it.
 */
const NoAdminAccessPage: React.FC = () => {
  const { user, loading, logout, resendVerification } = useAuth();
  const navigate = useNavigate();
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");
  const [resendError, setResendError] = useState("");

  // Not wrapped in ProtectedRoute, which sends unverified accounts here.
  if (!loading && !user) return <Navigate to="/login" replace />;

  const unverified = Boolean(user && !user.emailVerified);
  const who = user?.email ?? "This account";

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleResend = async () => {
    setResendError("");
    setResendState("sending");
    try {
      await resendVerification();
      setResendState("sent");
    } catch (err: any) {
      setResendError(getErrorMessage(err, "Could not send the email"));
      setResendState("idle");
    }
  };

  const buttonClass =
    "inline-flex items-center justify-center rounded-full bg-[#f3dfae] px-5 py-2 text-xs tracking-[0.18em] uppercase text-[#151A3A] font-ui font-bold disabled:opacity-60";

  return (
    <div className="px-4 pt-10 pb-24 flex justify-center">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 text-center">
        <h1 className="font-display text-2xl font-semibold text-slate-900">
          {unverified ? "Email not verified" : "No admin access"}
        </h1>
        <p className="mt-3 font-body text-sm text-slate-700 leading-relaxed">
          {unverified
            ? `${who} has not verified its email address. Open the link in the verification email, then sign in again.`
            : `${who} cannot use the admin area.`}
        </p>

        {unverified && resendState === "sent" && (
          <p className="mt-3 font-body text-sm text-slate-700">Verification email sent.</p>
        )}
        {resendError && <p className="mt-3 font-body text-sm text-[#e22e5a]">{resendError}</p>}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {unverified && (
            <button
              type="button"
              onClick={handleResend}
              disabled={resendState === "sending"}
              className={buttonClass}
            >
              Resend verification email
            </button>
          )}
          <button type="button" onClick={handleSignOut} className={buttonClass}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoAdminAccessPage;
